import React, { useState, useEffect } from 'react';
import { FaUsers, FaUserPlus, FaSearch, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as professorService from '../services/professorService';
import * as authService from '../services/authService';

const AvailableSubEBUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState(null);
    const [requestingId, setRequestingId] = useState(null);
    const [hasSelected, setHasSelected] = useState(false);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const response = await professorService.getAvailableSubEBUsers();
            // Support multiple response formats: { data: { users: [] } }, { users: [] }, or just []
            const data = response.data || response;
            const usersList = data.users || (Array.isArray(data) ? data : []);

            setUsers(usersList);
            setHasSelected(data.hasSelected || false);
        } catch (error) {
            console.error('Error loading available users:', error);
            showToast('Failed to load available users', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectUser = async (userId) => {
        if (hasSelected) {
            showToast('You have already selected a user for the Editorial Board.', 'error');
            return;
        }

        setRequestingId(userId);
        try {
            await professorService.requestSubEB(userId);
            showToast('Request sent to admin for verification.', 'success');
            await loadUsers(); // Await loadUsers to ensure hasSelected state is updated before clearing requestingId
        } catch (error) {
            console.error('Error requesting Sub-EB:', error);
            showToast(error.message || 'Failed to send request', 'error');
        } finally {
            setRequestingId(null);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const filteredUsers = hasSelected
        ? users.filter(user => user.is_requested || user.is_verified_sub_eb || user.id === requestingId).slice(0, 1)
        : users.filter((user) =>
            (user.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
        );

    const darkGradient = {
        background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading available users...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <div className="max-w mx-auto">
                {/* Header */}
                <div className="rounded-2xl shadow-lg p-8 mb-8" style={darkGradient}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                <FaUserPlus className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-white">Sub-EB Selection</h1>
                                <p className="text-blue-100 mt-1">
                                    {hasSelected
                                        ? (filteredUsers[0]?.is_verified_sub_eb
                                            ? "Your recommendation has been verified by the administrator."
                                            : "Your recommendation is currently pending verification by the administrator.")
                                        : "Select a user to recommend for the Editorial Board"}
                                </p>
                            </div>
                        </div>
                        {hasSelected && (
                            <div className="bg-green-400/20 border border-green-400/30 px-4 py-2 rounded-lg backdrop-blur-md">
                                <p className="text-green-400 font-semibold flex items-center gap-2">
                                    <FaCheckCircle /> Recommendation Locked
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Search - Hide when a user is already selected */}
                {!hasSelected && (
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
                        <div className="relative">
                            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search users by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all outline-none"
                            />
                        </div>
                    </div>
                )}

                {/* Users List */}
                {filteredUsers.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-lg p-16 text-center">
                        <FaUsers className="w-20 h-20 text-gray-200 mx-auto mb-6" />
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            {hasSelected ? "Loading your selection..." : "No Users Available"}
                        </h3>
                        <p className="text-gray-600 max-w-md mx-auto">
                            {!hasSelected && searchTerm
                                ? "No users match your search criteria. Try different keywords."
                                : !hasSelected ? "There are currently no users available for selection."
                                    : "Your selection details are being loaded."}
                        </p>
                    </div>
                ) : (
                    <div className={hasSelected ? "max-w-2xl mx-auto" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"}>
                        {filteredUsers.map((user) => (
                            <div
                                key={user.id}
                                className={`bg-white rounded-2xl shadow-md transition-all duration-300 border border-transparent hover:border-blue-100 overflow-hidden group ${hasSelected ? 'border-blue-200 ring-4 ring-blue-50 shadow-xl' : 'hover:shadow-xl'}`}
                            >
                                <div className="p-6">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg ${hasSelected ? 'bg-gradient-to-br from-green-600 to-green-700' : 'bg-gradient-to-br from-blue-600 to-indigo-700'}`}>
                                            {user.full_name ? user.full_name.charAt(0).toUpperCase() : '?'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-bold text-gray-900 truncate">
                                                {user.full_name || 'Anonymous User'}
                                            </h3>
                                            <p className="text-sm text-gray-600 truncate font-medium">{user.email}</p>
                                            {user.phone_number && (
                                                <p className="text-sm text-blue-600 font-bold mt-1 select-all">{user.phone_number}</p>
                                            )}
                                        </div>
                                    </div>

                                    {!hasSelected && (
                                        <div className="space-y-3 mb-6 border-t border-gray-100 pt-4">
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <span className="font-semibold text-gray-400">Role :</span>
                                                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                                                    {user.role || 'User'}
                                                </span>
                                            </div>
                                            {user.university && (
                                                <div className="flex items-start gap-2 text-sm text-gray-600">
                                                    <span className="font-semibold text-gray-400 flex-shrink-0">Inst. :</span>
                                                    <span className="truncate">{user.university}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {(user.is_requested || user.is_verified_sub_eb || (hasSelected && filteredUsers.some(u => u.id === user.id))) ? (
                                        <div className={`w-full py-4 rounded-xl font-bold flex flex-col items-center justify-center gap-2 border-2 transition-all ${user.is_verified_sub_eb
                                            ? 'bg-green-50 border-green-200 text-green-700'
                                            : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
                                            <div className="flex items-center gap-2 text-lg">
                                                {user.is_verified_sub_eb ? (
                                                    <><FaCheckCircle className="text-xl" /> Verified EB Member</>
                                                ) : (
                                                    <><FaHourglassHalf className="animate-pulse" /> Verification Pending</>
                                                )}
                                            </div>
                                            <p className="text-[10px] uppercase tracking-widest opacity-70 font-black">
                                                {user.is_verified_sub_eb ? 'Promotion Complete' : 'Awaiting Admin Approval'}
                                            </p>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleSelectUser(user.id)}
                                            disabled={requestingId === user.id}
                                            className={`w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${requestingId === user.id
                                                ? 'bg-blue-100 text-blue-600 cursor-wait'
                                                : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg transform hover:-translate-y-0.5'
                                                }`}
                                        >
                                            {requestingId === user.id ? (
                                                <>
                                                    <FaHourglassHalf className="animate-spin" />
                                                    Sending Request...
                                                </>
                                            ) : (
                                                <>
                                                    <FaUserPlus /> Recommend for EB
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AvailableSubEBUsers;
